/**
 * Backlink index (§4.10 / §4.15) — "mentioned in".
 *
 * Scans every description (module / surface / interface / type bodies,
 * process / subprocess / view declarations) and every `.md` document body
 * for `[[ … ]]` element references, then lets a target ask who points at
 * it. Built structurally (once per assemble / serialize), never on the hot
 * value-edit path.
 */

import { scanRefs, classifyRef, splitCodeRegions } from "./refs.js";
import type { MarkdownDocument } from "./document.js";
import type { SubprocessDecl } from "./../types.js";
import type {
  ResolvedModel,
  ResolvedModule,
  ResolvedSurface,
  ResolvedInterface,
} from "./../resolver.js";

export interface Backlink {
  /** What mentions the target. */
  readonly kind: "element" | "document";
  /** Display name of the mentioning element or document. */
  readonly name: string;
  /** Qualified name (elements) — undefined for documents. */
  readonly qualifiedName?: string;
  /** Source path (documents) — undefined for elements. */
  readonly path?: string;
}

export interface BacklinkIndex {
  /** Backlinks to an element referenced by `[[Name]]`. */
  forName(name: string): readonly Backlink[];
  /** Backlinks to an element referenced by `[[#id]]`. */
  forId(id: string): readonly Backlink[];
  /** Backlinks to a document referenced by `[[Doc#section]]` / `[[Doc]]`. */
  forDocument(name: string): readonly Backlink[];
}

interface Mention {
  readonly source: Backlink;
  readonly text: string;
}

export function buildBacklinkIndex(
  model: ResolvedModel,
  documents: readonly MarkdownDocument[] = []
): BacklinkIndex {
  const mentions: Mention[] = [];

  const addDescriptions = (
    source: Backlink,
    descriptions: readonly { text: string }[]
  ): void => {
    for (const d of descriptions) mentions.push({ source, text: d.text });
  };

  const visitInterface = (i: ResolvedInterface, parentQ: string): void => {
    const q = `${parentQ}.${i.name}`;
    addDescriptions({ kind: "element", name: i.name, qualifiedName: q }, i.body.descriptions);
  };
  const visitSurface = (s: ResolvedSurface, parentQ: string): void => {
    const q = `${parentQ}.${s.name}`;
    addDescriptions({ kind: "element", name: s.name, qualifiedName: q }, s.body.descriptions);
    for (const i of s.interfaces) visitInterface(i, q);
    for (const sub of s.surfaces) visitSurface(sub, q);
  };
  const visitModule = (m: ResolvedModule, parent: string): void => {
    const q = parent ? `${parent}.${m.name}` : m.name;
    addDescriptions({ kind: "element", name: m.name, qualifiedName: q }, m.body.descriptions);
    for (const i of m.interfaces) visitInterface(i, q);
    for (const s of m.surfaces) visitSurface(s, q);
    for (const c of m.modules) visitModule(c, q);
  };
  for (const m of model.modules) visitModule(m, "");

  // Process / subprocess / view / type descriptions are mention sources too —
  // §4.10 promises the reverse index covers ALL descriptions, and the shipped
  // pattern of a process description enumerating its [[participants]] relies
  // on it (r53). Anonymous processes carry an empty name — skip them (a blank
  // "mentioned in" row is worse than no row).
  const addFlat = (name: string, qualifiedName: string, text: string | undefined): void => {
    if (!name || !text) return;
    mentions.push({ source: { kind: "element", name, qualifiedName }, text });
  };
  const visitSub = (sp: SubprocessDecl, ownerQ: string): void => {
    const q = ownerQ ? `${ownerQ}.${sp.name}` : sp.name;
    addFlat(sp.name, q, sp.description);
    for (const n of sp.subprocesses ?? []) visitSub(n, q);
  };
  for (const p of model.processes) {
    const q = p.ownerPath && p.ownerPath.length > 0 ? [...p.ownerPath, p.name].join(".") : p.name;
    addFlat(p.name, q, p.description);
    for (const sp of p.subprocesses ?? []) visitSub(sp, q);
  }
  for (const sp of model.subprocesses) {
    visitSub(sp, sp.ownerPath && sp.ownerPath.length > 0 ? sp.ownerPath.join(".") : "");
  }
  for (const v of model.views) {
    const q = v.ownerPath && v.ownerPath.length > 0 ? [...v.ownerPath, v.name].join(".") : v.name;
    addFlat(v.name, q, v.description);
  }
  for (const t of model.types) {
    addDescriptions({ kind: "element", name: t.name, qualifiedName: t.name }, t.body.descriptions);
  }

  for (const doc of documents) {
    mentions.push({ source: { kind: "document", name: doc.name, path: doc.path }, text: doc.body });
  }

  const byName = new Map<string, Backlink[]>();
  const byId = new Map<string, Backlink[]>();
  const byDoc = new Map<string, Backlink[]>();
  const push = (map: Map<string, Backlink[]>, key: string, b: Backlink): void => {
    const list = map.get(key);
    // Dedupe: one mention source counts once per target.
    if (list) {
      if (!list.some((x) => x.kind === b.kind && x.qualifiedName === b.qualifiedName && x.path === b.path)) {
        list.push(b);
      }
    } else {
      map.set(key, [b]);
    }
  };

  // Names of `.md` documents, so a bare `[[Doc]]` (no `#section`) resolves to
  // the document rather than being treated only as an element name.
  const docNames = new Set(documents.map((d) => d.name));

  for (const { source, text } of mentions) {
    // Refs inside code (fenced blocks + inline spans) are literal — skip them,
    // matching how descriptions/documents render.
    for (const seg of splitCodeRegions(text)) {
      if (seg.kind !== "text") continue;
      for (const tok of scanRefs(seg.text)) {
        if (tok.kind !== "ref") continue;
        const ref = classifyRef(tok.inner);
        if (ref.kind === "id") push(byId, ref.id, source);
        else if (ref.kind === "section") push(byDoc, ref.doc, source);
        else if (ref.kind === "name") {
          // A bare name that matches a document is a document mention; both an
          // element and a document can legitimately share the bucket.
          if (docNames.has(ref.name)) push(byDoc, ref.name, source);
          push(byName, ref.name, source);
        }
      }
    }
  }

  return {
    forName: (name) => byName.get(name) ?? [],
    forId: (id) => byId.get(id) ?? [],
    forDocument: (name) => byDoc.get(name) ?? [],
  };
}
